/**
 * OMNITrade Strategies Page
 * Public page explaining all trading strategies used by the bot
 */

import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Activity, BarChart3, TrendingUp, Zap, Grid3X3,
  AlertTriangle, CheckCircle, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SyncLogoWithText } from '@/components/Logo';

// Warning box component
const Warning = ({ children }) => (
  <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg my-4">
    <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
    <div className="text-yellow-200 text-sm">{children}</div>
  </div>
);

// Tip box component
const Tip = ({ children }) => (
  <div className="flex items-start gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg my-4">
    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
    <div className="text-green-200 text-sm">{children}</div>
  </div>
);

// Card component
const Card = ({ children, className = '' }) => (
  <div className={`bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 ${className}`}>
    {children}
  </div>
);

const StrategiesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <SyncLogoWithText variant="dark" />
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-gray-300 hover:text-white transition-colors text-sm">Home</a>
              <a href="/interface-guide" className="text-gray-300 hover:text-white transition-colors text-sm">Interface Guide</a>
              <a href="/strategies" className="text-[#5A9BD5] font-medium text-sm">Strategies</a>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="text-gray-300 hover:text-white hover:bg-white/10"
                onClick={() => navigate('/auth')}
              >
                Log In
              </Button>
              <Button 
                className="bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white"
                onClick={() => navigate('/auth?mode=register')}
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white hover:bg-slate-700/50 transition"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-[#5A9BD5]" />
              Trading Strategies
            </h1>
            <p className="text-slate-400 text-sm">Understanding when each strategy works best</p>
          </div>
        </div>

        <Card className="mb-8">
          <p className="text-slate-300 mb-6">
            OMNITrade offers six different trading strategies. Each has its strengths depending on market conditions.
            Here's a beginner-friendly explanation of each:
          </p>

          {/* Mean Reversion RSI */}
          <div className="p-6 bg-slate-700/30 rounded-lg border border-blue-500/30 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Mean Reversion (RSI)</h3>
                <span className="px-2 py-0.5 text-xs bg-green-500 text-white rounded">Beginner Friendly</span>
              </div>
            </div>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">The Idea:</strong> Prices tend to return to their average over time. When something is 
              "oversold" (too cheap), it often bounces back up. When it's "overbought" (too expensive), it often drops.
            </p>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">How it Works:</strong> Uses the RSI (Relative Strength Index) indicator, which measures 
              how "oversold" or "overbought" an asset is on a scale of 0-100.
            </p>
            <ul className="text-slate-300 space-y-2 mb-4">
              <li>📈 <strong className="text-white">RSI below 30:</strong> Asset is oversold → Bot considers BUYING</li>
              <li>📉 <strong className="text-white">RSI above 70:</strong> Asset is overbought → Bot considers SELLING</li>
            </ul>
            <p className="text-green-300 mb-2">
              <strong>Best For:</strong> Sideways/ranging markets where prices bounce between highs and lows.
            </p>
            <p className="text-red-300">
              <strong>Avoid When:</strong> Markets are trending strongly in one direction.
            </p>
          </div>

          {/* Bollinger Bands */}
          <div className="p-6 bg-slate-700/30 rounded-lg border border-indigo-500/30 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Bollinger Bands</h3>
                <span className="px-2 py-0.5 text-xs bg-green-500 text-white rounded">Beginner Friendly</span>
              </div>
            </div>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">The Idea:</strong> Bollinger Bands create a "channel" around price using standard deviations. 
              Prices tend to stay within these bands and bounce off them - buy at the bottom, sell at the top.
            </p>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">How it Works:</strong> Three lines are drawn: a middle line (20-period moving average), an upper band 
              (2 standard deviations above), and a lower band (2 standard deviations below).
            </p>
            <ul className="text-slate-300 space-y-2 mb-4">
              <li>📈 <strong className="text-white">Price touches lower band:</strong> Oversold condition → Bot considers BUYING</li>
              <li>📉 <strong className="text-white">Price touches upper band:</strong> Overbought condition → Bot considers SELLING</li>
              <li>⚡ <strong className="text-white">Bands squeeze together:</strong> Low volatility, big move coming → Bot waits</li>
              <li>📊 <strong className="text-white">%B indicator:</strong> Shows exactly where price is relative to bands (0 = lower, 1 = upper)</li>
            </ul>
            <p className="text-green-300 mb-2">
              <strong>Best For:</strong> Ranging markets with clear support/resistance levels.
            </p>
            <p className="text-red-300">
              <strong>Avoid When:</strong> Strong trending markets (price can "walk the band" during trends).
            </p>
            <Tip>
              When Bollinger Bands squeeze together (low bandwidth), it often signals a big move is coming. 
              The strategy will wait during squeezes to avoid false signals.
            </Tip>
          </div>

          {/* MACD */}
          <div className="p-6 bg-slate-700/30 rounded-lg border border-rose-500/30 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-rose-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">MACD (Moving Average Convergence Divergence)</h3>
                <span className="px-2 py-0.5 text-xs bg-yellow-500 text-white rounded">Intermediate</span>
              </div>
            </div>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">The Idea:</strong> MACD shows the relationship between two moving averages. 
              When they cross, it signals a potential change in momentum and trend direction.
            </p>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">How it Works:</strong> Calculates the difference between a fast EMA (12-period) and slow EMA (26-period). 
              This forms the MACD line. A 9-period EMA of the MACD line creates the "Signal line".
            </p>
            <ul className="text-slate-300 space-y-2 mb-4">
              <li>📈 <strong className="text-white">Bullish Crossover:</strong> MACD crosses ABOVE signal line → Strong BUY signal</li>
              <li>📉 <strong className="text-white">Bearish Crossover:</strong> MACD crosses BELOW signal line → Strong SELL signal</li>
              <li>📊 <strong className="text-white">Histogram:</strong> Shows momentum strength (MACD - Signal). Growing = strengthening trend</li>
              <li>🔍 <strong className="text-white">Divergence:</strong> When price and MACD move opposite directions, reversal may be coming</li>
            </ul>
            <p className="text-green-300 mb-2">
              <strong>Best For:</strong> Trending markets with clear momentum. Great for catching trend reversals.
            </p>
            <p className="text-red-300">
              <strong>Avoid When:</strong> Choppy, sideways markets (produces many false crossovers).
            </p>
            <Tip>
              Watch for divergences! If price makes a new high but MACD makes a lower high, it's a warning sign. 
              The opposite (bullish divergence) can signal a bottom.
            </Tip>
          </div>

          {/* Breakout Strategy */}
          <div className="p-6 bg-slate-700/30 rounded-lg border border-purple-500/30 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Breakout Strategy</h3>
                <span className="px-2 py-0.5 text-xs bg-yellow-500 text-white rounded">Intermediate</span>
              </div>
            </div>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">The Idea:</strong> When prices break out of a trading range, they often continue moving 
              in that direction with momentum.
            </p>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">How it Works:</strong> Uses Donchian Channels (the highest high and lowest low over a period) 
              to identify when price breaks through these levels.
            </p>
            <ul className="text-slate-300 space-y-2 mb-4">
              <li>🚀 <strong className="text-white">Price breaks above channel:</strong> Potential upward breakout → Bot BUYS</li>
              <li>💥 <strong className="text-white">Price breaks below channel:</strong> Potential breakdown → Bot may SHORT</li>
              <li>⚡ <strong className="text-white">Squeeze detection:</strong> Low volatility often precedes big moves</li>
            </ul>
            <p className="text-green-300 mb-2">
              <strong>Best For:</strong> Volatile markets, news events, and trend beginnings.
            </p>
            <p className="text-red-300">
              <strong>Avoid When:</strong> Markets are choppy with many false breakouts.
            </p>
          </div>

          {/* Trend Following */}
          <div className="p-6 bg-slate-700/30 rounded-lg border border-green-500/30 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Trend Following</h3>
                <span className="px-2 py-0.5 text-xs bg-blue-500 text-white rounded">Intermediate</span>
              </div>
            </div>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">The Idea:</strong> "The trend is your friend." When markets are trending, it's often 
              profitable to follow that direction rather than fight it.
            </p>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">How it Works:</strong> Uses two Moving Averages (fast and slow) plus ADX (trend strength indicator).
            </p>
            <ul className="text-slate-300 space-y-2 mb-4">
              <li>📈 <strong className="text-white">Fast MA crosses above Slow MA:</strong> Uptrend signal → Bot BUYS</li>
              <li>📉 <strong className="text-white">Fast MA crosses below Slow MA:</strong> Downtrend signal → Bot SELLS</li>
              <li>💪 <strong className="text-white">ADX confirms strength:</strong> Only trades when trend is strong enough</li>
            </ul>
            <p className="text-green-300 mb-2">
              <strong>Best For:</strong> Clear trending markets (bull runs or bear markets).
            </p>
            <p className="text-red-300">
              <strong>Avoid When:</strong> Markets are moving sideways with no clear direction.
            </p>
          </div>

          {/* Grid Trading */}
          <div className="p-6 bg-slate-700/30 rounded-lg border border-cyan-500/30 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center">
                <Grid3X3 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Grid Trading</h3>
                <span className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded">Advanced</span>
              </div>
            </div>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">The Idea:</strong> Place multiple buy orders below current price and sell orders above. 
              Profit from price oscillations within a range.
            </p>
            <p className="text-slate-300 mb-4">
              <strong className="text-white">How it Works:</strong> Creates a "grid" of price levels. Buys when price drops to a level, 
              sells when it rises to the next level.
            </p>
            <ul className="text-slate-300 space-y-2 mb-4">
              <li>🔽 <strong className="text-white">Price drops to grid level:</strong> Bot places BUY order</li>
              <li>🔼 <strong className="text-white">Price rises to next level:</strong> Bot places SELL order</li>
              <li>🔄 <strong className="text-white">Cycle repeats:</strong> Profits from each up/down movement</li>
            </ul>
            <p className="text-green-300 mb-2">
              <strong>Best For:</strong> Sideways/ranging markets with consistent oscillations.
            </p>
            <p className="text-red-300 mb-4">
              <strong>Avoid When:</strong> Strong trending markets (can accumulate losses on wrong side).
            </p>
            <Warning>
              Grid trading requires more capital and careful configuration. Start with paper trading!
            </Warning>
          </div>

          {/* Strategy Selection Guide */}
          <div className="p-6 bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg border border-blue-500/30 mb-6">
            <h3 className="text-white font-semibold text-lg mb-4">📊 Quick Strategy Selection Guide</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-600">
                    <th className="text-left text-slate-300 pb-2">Market Condition</th>
                    <th className="text-left text-slate-300 pb-2">Recommended Strategy</th>
                    <th className="text-left text-slate-300 pb-2">Why</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  <tr className="border-b border-slate-700/50">
                    <td className="py-2">Sideways/Ranging</td>
                    <td className="py-2 text-green-400">RSI, Bollinger Bands</td>
                    <td className="py-2">Price bounces between levels</td>
                  </tr>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-2">Strong Uptrend</td>
                    <td className="py-2 text-green-400">Trend Following, MACD</td>
                    <td className="py-2">Ride the momentum</td>
                  </tr>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-2">Strong Downtrend</td>
                    <td className="py-2 text-green-400">Trend Following, MACD</td>
                    <td className="py-2">Short or stay out</td>
                  </tr>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-2">Low Volatility (Squeeze)</td>
                    <td className="py-2 text-green-400">Breakout</td>
                    <td className="py-2">Catch the expansion</td>
                  </tr>
                  <tr className="border-b border-slate-700/50">
                    <td className="py-2">High Volatility</td>
                    <td className="py-2 text-green-400">Grid Trading</td>
                    <td className="py-2">Profit from swings</td>
                  </tr>
                  <tr>
                    <td className="py-2">Unsure / New Trader</td>
                    <td className="py-2 text-green-400">RSI or Bollinger Bands</td>
                    <td className="py-2">Most forgiving strategies</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Tip>
            <strong>Pro Tip:</strong> No strategy works in all market conditions. The best approach is to 
            understand current market conditions and choose the appropriate strategy. When in doubt, 
            start with Mean Reversion RSI or Bollinger Bands as they're the most beginner-friendly.
          </Tip>
        </Card>

        {/* CTA */}
        <div className="text-center mt-12">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Start Trading?</h2>
          <p className="text-gray-400 mb-6">Sign up for free and download the desktop app to get started.</p>
          <Button 
            size="lg"
            className="bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white px-8"
            onClick={() => navigate('/auth?mode=register')}
          >
            Sign Up Free
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StrategiesPage;
